import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import RemoveIcon from "@mui/icons-material/Remove";
import ReplayIcon from "@mui/icons-material/Replay";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Snackbar,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import BoardMatColorSwitcher from "../../../pages/puzzles/jigsaw/BoardMatColorSwitcher";
import CanvasBoard from "../../../pages/puzzles/jigsaw/CanvasBoard";
import CanvasScattered, { buildScatterPositions } from "../../../pages/puzzles/jigsaw/CanvasScattered";
import PlayToolbarScrollRow from "../../../pages/components/PlayToolbarScrollRow";
import JigsawColorBasketPlayService from "../../../pages/puzzles/jigsaw/JigsawColorBasketPlayService";
import JigsawPlayService from "../../../pages/puzzles/jigsaw/JigsawPlayService";
import { PIECE_SHINE_CLEANUP_MS } from "../../../pages/puzzles/jigsaw/PieceLockShineOverlay";
import {
    findNewlyGluedPieceIds,
    isPieceExactlyAtHome,
    moveBoardPieceGroup,
    newPieceGroupId,
    placeBoardPieceWithAttach,
    snapMovedPiecesToHome,
    tryAttachNeighbors,
} from "../../../pages/puzzles/jigsaw/jigsawPieceGroups";
import {
    ensurePieceGlueStyle,
    PIECE_GLUE_CLEANUP_MS,
} from "../../../pages/puzzles/jigsaw/jigsawPieceGlueEffects";
import {
    BOARD_SIZE,
    PIECE_VISUAL_MARGIN_RATIO,
    PieceVisual,
    pieceVisualCellOffsetPx,
    pieceVisualRenderSizePx,
} from "../../../pages/puzzles/jigsaw/PieceVisual";
import { useBoardMatColor } from "../../../pages/puzzles/jigsaw/useBoardMatColor";
import AudioService, { AUDIO_EFFECT } from "../../../services/AudioService";
import Logger from "../../../services/Logger";
import AdminColorBasketMiniMap, {
    colorBasketNumberBadgeSx,
} from "./AdminColorBasketMiniMap";

const BOARD_ZOOM_MIN = 1;
const BOARD_ZOOM_MAX = 3;
const BOARD_ZOOM_STEP = 0.25;
const SAVE_DEBOUNCE_MS = 2000;
const PLAY_STATE_OPENED = "OPENED";

/** Promo capture tray: per-row piece size, so a 2-row tray stays a slim strip. */
const PROMO_TRAY_ROW_PX = 96;

/** Oversized controls for promo / OBS capture (not real-play compact UI). */
const PROMO_TOOLBAR_ICON_SX = {
    minWidth: 80,
    minHeight: 80,
    width: 80,
    height: 80,
    padding: "16px",
    boxSizing: "border-box",
    color: "#3BA666",
    // Beat MUI fontSize="small" / "medium" on nested icons.
    "& .MuiSvgIcon-root": { fontSize: "2.6rem !important" },
};

/** Match {@link JigsawPlayBoardPage} main play toolbar chrome. */
const PLAY_TOOLBAR_FG = "#1B4332";
const PLAY_TOOLBAR_ICON_BTN_SX = {
    padding: "6px",
    minWidth: 40,
    minHeight: 40,
    color: PLAY_TOOLBAR_FG,
    "& .MuiSvgIcon-root": { fontSize: "1.35rem" },
};
const PLAY_TOOLBAR_PILL_SX = {
    mb: 0,
    mt: 0,
    minWidth: 0,
    width: "auto",
    flex: "0 1 auto",
    ml: "auto",
    bgcolor: "rgba(255,255,255,0.78)",
    borderRadius: 2,
    px: 0.75,
    py: 0.35,
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
    border: "1px solid rgba(27, 67, 50, 0.12)",
};
const PLAY_STATE_DEPLOYED = "DEPLOYED";

function homePosition(pieceIndex, grid) {
    const cellPx = BOARD_SIZE / grid;
    return {
        x: (pieceIndex % grid) * cellPx,
        y: Math.floor(pieceIndex / grid) * cellPx,
    };
}

function pointInRect(clientX, clientY, el, pad = 0) {
    if (!el) return false;
    const r = el.getBoundingClientRect();
    return (
        clientX >= r.left - pad
        && clientX <= r.right + pad
        && clientY >= r.top - pad
        && clientY <= r.bottom + pad
    );
}

function boardLocalFromClient(clientX, clientY, boardEl, grabDx, grabDy) {
    const rect = boardEl.getBoundingClientRect();
    const s = rect.width / BOARD_SIZE || 1;
    return {
        x: (clientX - rect.left) / s - grabDx,
        y: (clientY - rect.top) / s - grabDy,
        scale: s,
    };
}

/**
 * Map pointer → board coords via the cropped viewport (more reliable than the
 * transformed full-board element when only a region is visible).
 */
function boardLocalFromCropViewport(clientX, clientY, viewportEl, bounds, grabDx, grabDy) {
    if (!viewportEl || !bounds || bounds.width <= 0 || bounds.height <= 0) return null;
    const vr = viewportEl.getBoundingClientRect();
    if (!(vr.width > 0) || !(vr.height > 0)) return null;
    const fitScale = Math.min(vr.width / bounds.width, vr.height / bounds.height);
    if (!(fitScale > 0)) return null;
    const padX = (vr.width - bounds.width * fitScale) / 2;
    const padY = (vr.height - bounds.height * fitScale) / 2;
    return {
        x: (clientX - vr.left - padX) / fitScale + bounds.left - grabDx,
        y: (clientY - vr.top - padY) / fitScale + bounds.top - grabDy,
        scale: fitScale,
    };
}

/** Cell origin for a list-tray piece whose circle is at screen coords (matches main play board). */
function listTrayDropCellOrigin(circleLeft, circleTop, circleSizePx, boardEl, cellPx) {
    if (!boardEl || !(circleSizePx > 0) || !(cellPx > 0)) return null;
    const rect = boardEl.getBoundingClientRect();
    const s = rect.width / BOARD_SIZE || 1;
    const pieceScreen = pieceVisualRenderSizePx(cellPx) * s;
    const pieceLeft = circleLeft + (circleSizePx - pieceScreen) / 2;
    const pieceTop = circleTop + (circleSizePx - pieceScreen) / 2;
    const tabOffset = pieceVisualCellOffsetPx(cellPx);
    return {
        x: (pieceLeft - rect.left) / s + tabOffset,
        y: (pieceTop - rect.top) / s + tabOffset,
    };
}

function newBasketBoardPieceId(pieceIndex) {
    return `b-${pieceIndex}-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
}

/** Scatter positions for basket pieces that live in the tray (not on the basket board). */
function buildBasketTrayScatter(pieceIds, grid, seedKey) {
    const maxId = pieceIds.length > 0 ? Math.max(...pieceIds) : 0;
    const positions = buildScatterPositions(maxId + 1, grid, seedKey);
    const out = {};
    pieceIds.forEach((pieceIndex, i) => {
        out[pieceIndex] = positions[pieceIndex] || {
            x: (i % Math.max(grid, 1)) * (BOARD_SIZE / Math.max(grid, 1)) * 0.35,
            y: Math.floor(i / Math.max(grid, 1)) * (BOARD_SIZE / Math.max(grid, 1)) * 0.35,
        };
    });
    return out;
}

function serializeBoardPositions(pieces) {
    return pieces.map((b) => ({
        id: b.id,
        pieceIndex: b.pieceIndex,
        x: b.x,
        y: b.y,
        ...(b.groupId ? { groupId: b.groupId } : {}),
    }));
}

function normalizeBoardPieceList(raw, idPrefix = "b") {
    if (!Array.isArray(raw)) return [];
    const stamp = Date.now();
    return raw
        .map((b, i) => {
            const pieceIndex = Number(b?.pieceIndex);
            if (!Number.isInteger(pieceIndex) || pieceIndex < 0) return null;
            const x = Number(b?.x);
            const y = Number(b?.y);
            if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
            return {
                id:
                    typeof b?.id === "string" && b.id
                        ? b.id
                        : `${idPrefix}-${pieceIndex}-${stamp}-${i}`,
                pieceIndex,
                x,
                y,
                ...(typeof b?.groupId === "string" && b.groupId ? { groupId: b.groupId } : {}),
            };
        })
        .filter(Boolean);
}

function normalizeSavedBoardPieces(raw, pieceIds, transferredIds) {
    if (!Array.isArray(raw)) return null;
    const transferred = new Set(
        (Array.isArray(transferredIds) ? transferredIds : []).map(Number).filter(Number.isInteger),
    );
    const remainingIds = pieceIds.filter((id) => !transferred.has(id));
    if (remainingIds.length === 0) return [];
    const allowed = new Set(remainingIds);
    return normalizeBoardPieceList(raw, "b").filter((b) => allowed.has(b.pieceIndex));
}

function normalizeScatteredMap(raw, puzzlePieceCount, gridN, onBoardIds, fallbackSeed) {
    const base = buildScatterPositions(puzzlePieceCount, gridN, fallbackSeed);
    const out = {};
    for (let i = 0; i < puzzlePieceCount; i += 1) {
        if (onBoardIds?.has?.(i)) continue;
        out[i] = base[i] || { x: 0, y: 0 };
    }
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
        Object.keys(raw).forEach((key) => {
            const pieceIndex = Number(key);
            if (!Number.isInteger(pieceIndex) || onBoardIds?.has?.(pieceIndex)) return;
            const x = Number(raw[key]?.x);
            const y = Number(raw[key]?.y);
            if (Number.isFinite(x) && Number.isFinite(y)) {
                out[pieceIndex] = { x, y };
            }
        });
    }
    return out;
}

function extractMainBoardPositions(apiData) {
    if (!apiData || typeof apiData !== "object") return null;
    const raw =
        apiData.mainBoardPositions
        ?? apiData.mainPlay?.boardPositions
        ?? apiData.boardPositions;
    return Array.isArray(raw) ? raw : null;
}

function largestConnectedGroup(pieces) {
    const byGroup = new Map();
    for (const b of pieces) {
        if (!b.groupId) continue;
        const list = byGroup.get(b.groupId) || [];
        list.push(b);
        byGroup.set(b.groupId, list);
    }
    let best = null;
    for (const [groupId, members] of byGroup) {
        if (members.length < 2) continue;
        if (!best || members.length > best.members.length) {
            best = { groupId, members };
        }
    }
    return best;
}

/**
 * Interactive one-basket practice: mini-map + board.
 * Progress → jigsaw_color_basket_play (separate from main jigsaw_play).
 */
export default function AdminColorBasketPlayPanel({
    basket,
    allBaskets,
    basketCount,
    jigsawId,
    grid,
    imageUrl,
    paths,
    svgPathFallback,
    onClose,
    hideMainBoard = false,
    hideCloseButton = false,
    onMainBoardSynced,
    /** Public play: piece map + board/tray + zoom + deploy only. */
    simpleMode = false,
    /**
     * Promo / OBS capture layout — oversized toolbar + 2-row tray (not real-play compact UI).
     * Intended for recording popups only.
     */
    promoCapture = false,
    /** When true (admin), show All-fit even in simpleMode. */
    isAdminRole = false,
    /** Called from the post-deploy dialog to leave basket practice. */
    onBackToMainPlay,
    /** Puzzle title shown next to back on simpleMode toolbar. */
    puzzleTitle = "",
    /** Optional controls before the Basket label (e.g. back). */
    toolbarLeading = null,
    /** Optional controls after basket info (e.g. timer, sweep). */
    toolbarMid = null,
    /** Optional controls at the far end (e.g. settings). */
    toolbarEnd = null,
    /** Optional override so fixed-size capture frames can center dialogs in the frame. */
    dialogSx = null,
    /**
     * Parent-owned ref that receives a flush function; awaiting it persists any pending
     * basket + main-board saves. Lets the parent leave basket practice without losing progress.
     */
    flushRef = null,
    /** Mirrors the main board mute switch for the piece-snap effect. */
    soundEffectsEnabled = true,
    /**
     * Called with the piece indexes resting exactly at home whenever basket board pieces settle,
     * so a parent main board can show that progress before the basket is finished and deployed.
     */
    onBasketHomePieces = null,
}) {
    const boardRef = useRef(null);
    const boardViewportRef = useRef(null);
    /** Scroll container around the zoomed board — the element drag-to-pan moves. */
    const boardScrollRef = useRef(null);
    const boardPanRef = useRef(null);
    const basketTrayRef = useRef(null);
    const mainBoardRef = useRef(null);
    const scatteredRef = useRef(null);
    const dragGhostRef = useRef(null);
    const persistReadyRef = useRef(false);
    const autoSaveRef = useRef(null);
    const boardPiecesRef = useRef([]);
    const mainBoardPiecesRef = useRef([]);
    const mainBoardIdsRef = useRef(new Set());
    const scatterPositionsStateRef = useRef({});
    const { mat: boardMat, matId: boardMatId, setMatId: setBoardMatId, grooveStyle: boardGrooveStyle } =
        useBoardMatColor();

    const pieceIds = useMemo(() => {
        const raw = Array.isArray(basket?.pieceIds) ? basket.pieceIds : [];
        return raw.map(Number).filter((id) => Number.isInteger(id) && id >= 0);
    }, [basket]);

    const hex = basket?.dominantColorHex || "#999999";
    const basketIndex = basket?.index;
    const resolvedBasketCount =
        Number(basketCount) > 0 ? Number(basketCount) : allBaskets?.length || 0;
    const puzzlePieceCount = grid > 0 ? grid * grid : 0;

    const [boardPieces, setBoardPieces] = useState([]);
    const [basketScatterPositions, setBasketScatterPositions] = useState({});
    const [mainBoardPieces, setMainBoardPieces] = useState([]);
    const [scatterPositions, setScatterPositions] = useState({});
    const [dragging, setDragging] = useState(null);
    const [activePieceShines, setActivePieceShines] = useState([]);
    const shineTimersRef = useRef([]);
    /** boardId → one-shot key for the "these pieces are glued" flash */
    const [pieceGlueKeys, setPieceGlueKeys] = useState({});
    const glueTimersRef = useRef([]);
    const [boardZoom, setBoardZoom] = useState(1);
    const [boardFullscreen, setBoardFullscreen] = useState(false);
    const [miniMapDialogOpen, setMiniMapDialogOpen] = useState(false);
    const [saveStatus, setSaveStatus] = useState("idle");
    const [sessionReady, setSessionReady] = useState(false);
    const [activeGroupId, setActiveGroupId] = useState(null);
    const [placeBusy, setPlaceBusy] = useState(false);
    const [deployBusy, setDeployBusy] = useState(false);
    const [playState, setPlayState] = useState(PLAY_STATE_OPENED);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });
    const [deployDoneDialog, setDeployDoneDialog] = useState({
        open: false,
        message: "",
        pieces: [],
        placedCount: 0,
    });

    const cellPx = grid > 0 ? BOARD_SIZE / grid : 0;

    const soundEffectsEnabledRef = useRef(soundEffectsEnabled);
    soundEffectsEnabledRef.current = soundEffectsEnabled;

    // Same snap effect as the main board, so a basket drop sounds like a main board drop.
    useEffect(() => {
        AudioService.preload(AUDIO_EFFECT.JIGSAW_PIECE_SNAP);
    }, []);

    const homeCount = useMemo(
        () =>
            boardPieces.filter((b) =>
                grid > 0 && cellPx > 0 ? isPieceExactlyAtHome(b, grid, cellPx) : false,
            ).length,
        [boardPieces, grid, cellPx],
    );
    const basketTotal = pieceIds.length;
    const isSolved =
        basketTotal > 0 && boardPieces.length === basketTotal && homeCount === basketTotal;

    const celebrateHomePlacement = useCallback((previous, next) => {
        const previousHomeIds = new Set(
            previous
                .filter((piece) => isPieceExactlyAtHome(piece, grid, cellPx))
                .map((piece) => piece.id),
        );
        const newlyHome = next.filter(
            (piece) =>
                !previousHomeIds.has(piece.id) && isPieceExactlyAtHome(piece, grid, cellPx),
        );
        if (newlyHome.length === 0) return;
        if (soundEffectsEnabledRef.current) {
            void AudioService.play(AUDIO_EFFECT.JIGSAW_PIECE_SNAP);
        }
        const shines = newlyHome.map((piece) => ({
            id: `basket-home-${piece.id}-${Date.now()}-${Math.random()}`,
            boardId: piece.id,
            pieceIndex: piece.pieceIndex,
        }));
        setActivePieceShines((current) => [...current, ...shines]);
        shineTimersRef.current.push(
            window.setTimeout(() => {
                const shineIds = new Set(shines.map((shine) => shine.id));
                setActivePieceShines((current) =>
                    current.filter((shine) => !shineIds.has(shine.id)),
                );
            }, PIECE_SHINE_CLEANUP_MS),
        );
    }, [cellPx, grid]);

    /** Glued clusters keep a standing outline; this is the burst at the moment they join. */
    const flashGluedPieces = useCallback(
        (previous, next) => {
            const gluedIds = findNewlyGluedPieceIds(previous, next, grid, cellPx);
            if (gluedIds.length === 0) return;
            ensurePieceGlueStyle();
            const stamp = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
            const keys = new Map(gluedIds.map((id) => [id, `${id}-${stamp}`]));
            setPieceGlueKeys((current) => {
                const merged = { ...current };
                for (const [id, key] of keys) merged[id] = key;
                return merged;
            });
            glueTimersRef.current.push(
                window.setTimeout(() => {
                    setPieceGlueKeys((current) => {
                        const merged = { ...current };
                        let changed = false;
                        for (const [id, key] of keys) {
                            if (merged[id] !== key) continue;
                            delete merged[id];
                            changed = true;
                        }
                        return changed ? merged : current;
                    });
                }, PIECE_GLUE_CLEANUP_MS),
            );
        },
        [cellPx, grid],
    );

    useEffect(
        () => () => {
            shineTimersRef.current.forEach((timer) => window.clearTimeout(timer));
            shineTimersRef.current = [];
            glueTimersRef.current.forEach((timer) => window.clearTimeout(timer));
            glueTimersRef.current = [];
        },
        [],
    );

    const mainOnBoardIds = useMemo(
        () => new Set(mainBoardPieces.map((b) => b.pieceIndex)),
        [mainBoardPieces],
    );

    boardPiecesRef.current = boardPieces;
    mainBoardPiecesRef.current = mainBoardPieces;
    mainBoardIdsRef.current = mainOnBoardIds;
    scatterPositionsStateRef.current = scatterPositions;

    const piecesInBasketTray = useMemo(() => {
        const onBoard = new Set(boardPieces.map((b) => b.pieceIndex));
        return pieceIds.filter((id) => !onBoard.has(id) && !mainOnBoardIds.has(id));
    }, [boardPieces, mainOnBoardIds, pieceIds]);

    const regionGuide = useMemo(
        () => ({
            pieceIndexes: pieceIds,
            colorHex: hex,
        }),
        [hex, pieceIds],
    );

    const regionBoundsPx = useMemo(() => {
        if (!grid || grid <= 0 || pieceIds.length === 0) return null;
        const cell = BOARD_SIZE / grid;
        let minCol = Infinity;
        let minRow = Infinity;
        let maxCol = -Infinity;
        let maxRow = -Infinity;
        for (const id of pieceIds) {
            const col = id % grid;
            const row = Math.floor(id / grid);
            minCol = Math.min(minCol, col);
            minRow = Math.min(minRow, row);
            maxCol = Math.max(maxCol, col);
            maxRow = Math.max(maxRow, row);
        }
        if (!Number.isFinite(minCol)) return null;
        return {
            left: minCol * cell,
            top: minRow * cell,
            right: (maxCol + 1) * cell,
            bottom: (maxRow + 1) * cell,
            cell,
        };
    }, [grid, pieceIds]);

    /**
     * Crop viewport + drop clamp — larger than the home region so pieces can sit outside it.
     * Promo capture keeps only the tab overhang so the guided region fills the frame height.
     */
    const basketCropPadCells = promoCapture
        ? PIECE_VISUAL_MARGIN_RATIO
        : simpleMode
          ? 3
          : 1.5;
    const basketCropBoundsPx = useMemo(() => {
        if (!regionBoundsPx || !(grid > 0)) return null;
        const cell = BOARD_SIZE / grid;
        const pad = cell * basketCropPadCells;
        const left = Math.max(0, regionBoundsPx.left - pad);
        const top = Math.max(0, regionBoundsPx.top - pad);
        const right = Math.min(BOARD_SIZE, regionBoundsPx.right + pad);
        const bottom = Math.min(BOARD_SIZE, regionBoundsPx.bottom + pad);
        return {
            left,
            top,
            right,
            bottom,
            width: right - left,
            height: bottom - top,
        };
    }, [regionBoundsPx, grid, basketCropPadCells]);

    const piecesNotOnMain = useMemo(() => {
        const out = [];
        for (let i = 0; i < puzzlePieceCount; i += 1) {
            if (!mainOnBoardIds.has(i)) out.push(i);
        }
        return out;
    }, [mainOnBoardIds, puzzlePieceCount]);
    const mainHomeCount = useMemo(
        () =>
            mainBoardPieces.filter((b) =>
                grid > 0 && cellPx > 0 ? isPieceExactlyAtHome(b, grid, cellPx) : false,
            ).length,
        [cellPx, grid, mainBoardPieces],
    );
    const mainSolved = puzzlePieceCount > 0 && mainHomeCount === puzzlePieceCount;

    const placeableGroup = useMemo(() => {
        if (activeGroupId) {
            const members = boardPieces.filter((b) => b.groupId === activeGroupId);
            if (members.length >= 2) return { groupId: activeGroupId, members };
        }
        return largestConnectedGroup(boardPieces);
    }, [activeGroupId, boardPieces]);

    const mainPersistReadyRef = useRef(false);
    const mainAutoSaveRef = useRef(null);
    /** Latest main-board payload waiting on the debounce, so it can be flushed on exit. */
    const pendingMainSaveRef = useRef(null);

    const ensureMainPlaySession = useCallback(async () => {
        if (!jigsawId || puzzlePieceCount <= 0 || grid <= 0) {
            return { success: false, error: "Main play unavailable" };
        }
        let result = await JigsawPlayService.start({ jigsawId });
        if (result.success) return result;

        const scatter = buildScatterPositions(
            puzzlePieceCount,
            grid,
            `main-tray-${jigsawId}`,
        );
        const scatteredPayload = {};
        for (let i = 0; i < puzzlePieceCount; i += 1) {
            scatteredPayload[i] = scatter[i] || { x: 0, y: 0 };
        }
        result = await JigsawPlayService.start({
            jigsawId,
            scatteredPieces: scatteredPayload,
            boardPositions: [],
        });
        return result;
    }, [grid, jigsawId, puzzlePieceCount]);

    const saveMainProgress = useCallback(
        async (pieces, scatter) => {
            if (!jigsawId) return;
            const onBoard = new Set(pieces.map((b) => b.pieceIndex));
            const scatteredPayload = {};
            for (let i = 0; i < puzzlePieceCount; i += 1) {
                if (onBoard.has(i)) continue;
                scatteredPayload[i] = scatter[i] || { x: 0, y: 0 };
            }
            const result = await JigsawPlayService.progress({
                jigsawId,
                boardPositions: serializeBoardPositions(pieces),
                scatteredPieces: scatteredPayload,
            });
            if (!result.success) {
                Logger.warn("Main play save failed", result.error);
            }
        },
        [jigsawId, puzzlePieceCount],
    );

    const scheduleMainAutoSave = useCallback(
        (pieces, scatter) => {
            if (!jigsawId || !mainPersistReadyRef.current) return;
            pendingMainSaveRef.current = { pieces, scatter };
            if (mainAutoSaveRef.current) clearTimeout(mainAutoSaveRef.current);
            mainAutoSaveRef.current = setTimeout(() => {
                mainAutoSaveRef.current = null;
                const pending = pendingMainSaveRef.current;
                pendingMainSaveRef.current = null;
                if (pending) {
                    void saveMainProgress(pending.pieces, pending.scatter);
                }
            }, SAVE_DEBOUNCE_MS);
        },
        [jigsawId, saveMainProgress],
    );

    // Main board + tray: resume jigsaw_play so deploy/place-group survives reload.
    useEffect(() => {
        let cancelled = false;
        mainPersistReadyRef.current = false;
        if (puzzlePieceCount <= 0 || !jigsawId || grid <= 0) {
            setMainBoardPieces([]);
            setScatterPositions({});
            return undefined;
        }

        (async () => {
            const fallbackScatter = buildScatterPositions(
                puzzlePieceCount,
                grid,
                `main-tray-${jigsawId}`,
            );
            const result = await ensureMainPlaySession();
            if (cancelled) return;
            if (!result.success || !result.data) {
                Logger.warn("Color basket practice: could not load main play", result.error);
                setMainBoardPieces([]);
                setScatterPositions(fallbackScatter);
                mainPersistReadyRef.current = false;
                return;
            }
            const board = normalizeBoardPieceList(result.data.boardPositions, "main");
            const onBoard = new Set(board.map((b) => b.pieceIndex));
            setMainBoardPieces(board);
            setScatterPositions(
                normalizeScatteredMap(
                    result.data.scatteredPieces,
                    puzzlePieceCount,
                    grid,
                    onBoard,
                    `main-tray-${jigsawId}-restored`,
                ),
            );
            mainPersistReadyRef.current = true;
        })();

        return () => {
            cancelled = true;
            mainPersistReadyRef.current = false;
            if (mainAutoSaveRef.current) {
                clearTimeout(mainAutoSaveRef.current);
                mainAutoSaveRef.current = null;
            }
            // Home pieces are mirrored by basket PUT …/progress; do not PUT a stale main layout.
            pendingMainSaveRef.current = null;
        };
    }, [ensureMainPlaySession, grid, jigsawId, puzzlePieceCount, saveMainProgress]);

    useEffect(() => {
        if (!mainPersistReadyRef.current) return;
        scheduleMainAutoSave(mainBoardPieces, scatterPositions);
    }, [mainBoardPieces, scatterPositions, scheduleMainAutoSave]);

    const ensureBasketPlaySession = useCallback(
        async (initialPieces) => {
            if (!jigsawId || resolvedBasketCount < 1 || basketIndex == null) return null;
            let result = await JigsawColorBasketPlayService.start({
                jigsawId,
                basketCount: resolvedBasketCount,
                basketIndex,
            });
            if (!result.success) {
                result = await JigsawColorBasketPlayService.start({
                    jigsawId,
                    basketCount: resolvedBasketCount,
                    basketIndex,
                    boardPositions: serializeBoardPositions(initialPieces),
                });
            }
            if (!result.success) {
                Logger.warn("Color basket play: could not start session", result.error);
                return null;
            }
            return result.data;
        },
        [basketIndex, jigsawId, resolvedBasketCount],
    );

    const saveProgress = useCallback(
        async (pieces, solved) => {
            if (!jigsawId || !persistReadyRef.current) return;
            setSaveStatus("saving");
            const result = await JigsawColorBasketPlayService.progress({
                jigsawId,
                basketCount: resolvedBasketCount,
                basketIndex,
                boardPositions: serializeBoardPositions(pieces),
                isSolved: solved,
            });
            if (result.success) {
                setSaveStatus("saved");
            } else {
                Logger.warn("Color basket play save failed", result.error);
                setSaveStatus("error");
            }
        },
        [basketIndex, jigsawId, resolvedBasketCount],
    );

    const scheduleAutoSave = useCallback(
        (pieces, solved) => {
            if (!jigsawId || !persistReadyRef.current) return;
            if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
            autoSaveRef.current = setTimeout(() => {
                saveProgress(pieces, solved);
            }, SAVE_DEBOUNCE_MS);
        },
        [jigsawId, saveProgress],
    );

    /**
     * Persists pending basket progress immediately so the backend can mirror home pieces
     * onto main before the parent leaves basket practice. Does not PUT main layout here —
     * a stale main payload would overwrite the server-side mirror.
     */
    const flushPendingSaves = useCallback(async () => {
        if (autoSaveRef.current) {
            clearTimeout(autoSaveRef.current);
            autoSaveRef.current = null;
        }
        if (mainAutoSaveRef.current) {
            clearTimeout(mainAutoSaveRef.current);
            mainAutoSaveRef.current = null;
        }
        pendingMainSaveRef.current = null;

        if (!persistReadyRef.current) return;
        const pieces = boardPiecesRef.current;
        const solved =
            pieceIds.length > 0
            && pieces.length === pieceIds.length
            && pieces.every((b) => isPieceExactlyAtHome(b, grid, cellPx));
        await saveProgress(pieces, solved);
    }, [cellPx, grid, pieceIds.length, saveProgress]);

    useEffect(() => {
        if (!flushRef) return undefined;
        flushRef.current = flushPendingSaves;
        return () => {
            if (flushRef.current === flushPendingSaves) {
                flushRef.current = null;
            }
        };
    }, [flushRef, flushPendingSaves]);

    useEffect(() => {
        let cancelled = false;
        persistReadyRef.current = false;
        setSessionReady(false);
        setSaveStatus("idle");
        setDragging(null);
        setBoardZoom(1);
        setActiveGroupId(null);
        setPlayState(PLAY_STATE_OPENED);

        (async () => {
            // Fresh sessions start with an empty board; pieces live in the tray.
            let pieces = [];
            let trayScatter = buildBasketTrayScatter(
                pieceIds,
                grid,
                `basket-tray-${basketIndex}`,
            );
            let canPersist = false;
            let nextPlayState = PLAY_STATE_OPENED;

            if (jigsawId && resolvedBasketCount >= 1 && basketIndex != null) {
                const session = await ensureBasketPlaySession([]);
                if (cancelled) return;
                if (session) {
                    const transferred = new Set(
                        (Array.isArray(session.transferredPieceIds)
                            ? session.transferredPieceIds
                            : []
                        )
                            .map(Number)
                            .filter(Number.isInteger),
                    );
                    const restored = normalizeSavedBoardPieces(
                        session.boardPositions,
                        pieceIds,
                        session.transferredPieceIds,
                    );
                    if (Array.isArray(restored)) {
                        pieces = restored;
                    }
                    const onBoard = new Set(pieces.map((p) => p.pieceIndex));
                    // Deployed/placed pieces stay off the basket tray (they're on main).
                    const trayIds = pieceIds.filter(
                        (id) => !onBoard.has(id) && !transferred.has(id),
                    );
                    trayScatter = buildBasketTrayScatter(
                        trayIds,
                        grid,
                        `basket-tray-${basketIndex}-restored`,
                    );
                    nextPlayState =
                        session.playState === PLAY_STATE_DEPLOYED
                            ? PLAY_STATE_DEPLOYED
                            : PLAY_STATE_OPENED;
                    canPersist = true;
                } else {
                    setSaveStatus("error");
                }
            }

            if (cancelled) return;
            // Keep basket-board layout; pieces already on main are mirrored next
            // (home-correct ones) so they still appear in basket practice.
            setBoardPieces(pieces);
            setBasketScatterPositions(trayScatter);
            setPlayState(nextPlayState);
            persistReadyRef.current = canPersist;
            setSessionReady(true);
        })();

        return () => {
            cancelled = true;
            if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
        };
    }, [
        basketIndex,
        ensureBasketPlaySession,
        grid,
        jigsawId,
        pieceIds,
        resolvedBasketCount,
    ]);

    // Mirror main-board home pieces onto the basket board so already-placed
    // basket pieces are visible when practicing this basket.
    useEffect(() => {
        if (!sessionReady || grid <= 0 || cellPx <= 0 || pieceIds.length === 0) return;
        if (!Array.isArray(mainBoardPieces) || mainBoardPieces.length === 0) return;

        const mainAtHome = new Map();
        for (const b of mainBoardPieces) {
            const idx = Number(b?.pieceIndex);
            if (!Number.isInteger(idx)) continue;
            if (!pieceIds.includes(idx)) continue;
            if (!isPieceExactlyAtHome(b, grid, cellPx)) continue;
            mainAtHome.set(idx, b);
        }
        if (mainAtHome.size === 0) return;

        setBoardPieces((prev) => {
            const onBoard = new Set(prev.map((p) => p.pieceIndex));
            const toAdd = [];
            for (const pieceIndex of mainAtHome.keys()) {
                if (onBoard.has(pieceIndex)) continue;
                const home = homePosition(pieceIndex, grid);
                toAdd.push({
                    id: newBasketBoardPieceId(pieceIndex),
                    pieceIndex,
                    x: home.x,
                    y: home.y,
                });
            }
            if (toAdd.length === 0) return prev;
            const merged = [...prev, ...toAdd];
            const { pieces } = tryAttachNeighbors(merged, merged[0]?.id, grid, cellPx);
            return pieces;
        });
    }, [sessionReady, mainBoardPieces, pieceIds, grid, cellPx]);

    // Basket home pieces are mirrored onto main by the backend on PUT …/progress.
    // Parent main boards (recording capture, leave → resume) learn about them after flush + reload.

    // Instant preview for a parent that renders its own main board (e.g. recording capture).
    useEffect(() => {
        if (!onBasketHomePieces || grid <= 0 || cellPx <= 0 || dragging) return;
        const homeIndexes = boardPieces
            .filter((b) => isPieceExactlyAtHome(b, grid, cellPx))
            .map((b) => b.pieceIndex);
        if (homeIndexes.length === 0) return;
        onBasketHomePieces(homeIndexes);
    }, [boardPieces, cellPx, dragging, grid, onBasketHomePieces]);

    useEffect(() => {
        if (!sessionReady || !persistReadyRef.current) return;
        const solved =
            pieceIds.length > 0 &&
            boardPieces.length === pieceIds.length &&
            boardPieces.every((b) => isPieceExactlyAtHome(b, grid, cellPx));
        scheduleAutoSave(boardPieces, solved);
    }, [boardPieces, cellPx, grid, pieceIds.length, scheduleAutoSave, sessionReady]);

    const snapAllHome = useCallback(() => {
        setBoardPieces((prev) => {
            if (prev.length === 0) return prev;
            const snapped = prev.map((b) => {
                const home = homePosition(b.pieceIndex, grid);
                return { ...b, x: home.x, y: home.y };
            });
            const { pieces } = tryAttachNeighbors(snapped, snapped[0]?.id, grid, cellPx);
            return pieces;
        });
    }, [cellPx, grid]);

    /** Move every tray piece onto the basket board at random positions (for testing). */
    const placeAllOnBoard = useCallback(() => {
        if (grid <= 0 || pieceIds.length === 0) return;
        const onMain = mainBoardIdsRef.current;
        const stamp = Date.now();
        const minX = regionBoundsPx ? regionBoundsPx.left : 0;
        const minY = regionBoundsPx ? regionBoundsPx.top : 0;
        const maxX = regionBoundsPx
            ? Math.max(minX, regionBoundsPx.right - cellPx)
            : Math.max(0, BOARD_SIZE - cellPx);
        const maxY = regionBoundsPx
            ? Math.max(minY, regionBoundsPx.bottom - cellPx)
            : Math.max(0, BOARD_SIZE - cellPx);
        const spanX = Math.max(0, maxX - minX);
        const spanY = Math.max(0, maxY - minY);

        setBoardPieces((prev) => {
            const onBoard = new Set(prev.map((b) => b.pieceIndex));
            const toAdd = pieceIds.filter((id) => !onBoard.has(id) && !onMain.has(id));
            if (toAdd.length === 0) return prev;
            const scatter = buildBasketTrayScatter(
                toAdd,
                grid,
                `basket-board-all-${basketIndex}-${stamp}`,
            );
            const added = toAdd.map((pieceIndex) => {
                const pos = scatter[pieceIndex];
                const x = Number.isFinite(pos?.x)
                    ? minX + (pos.x / BOARD_SIZE) * spanX
                    : minX + Math.random() * spanX;
                const y = Number.isFinite(pos?.y)
                    ? minY + (pos.y / BOARD_SIZE) * spanY
                    : minY + Math.random() * spanY;
                return {
                    id: newBasketBoardPieceId(pieceIndex),
                    pieceIndex,
                    x: Math.max(minX, Math.min(maxX, x)),
                    y: Math.max(minY, Math.min(maxY, y)),
                };
            });
            return [...prev, ...added];
        });
        setDragging(null);
        setActiveGroupId(null);
    }, [basketIndex, cellPx, grid, pieceIds, regionBoundsPx]);

    /** Place every tray piece on the board at home and attach (for testing). */
    const placeAllFitOnBoard = useCallback(() => {
        if (grid <= 0 || pieceIds.length === 0) return;
        const onMain = mainBoardIdsRef.current;
        setBoardPieces((prev) => {
            const onBoard = new Set(prev.map((b) => b.pieceIndex));
            const toAdd = pieceIds.filter((id) => !onBoard.has(id) && !onMain.has(id));
            if (toAdd.length === 0 && prev.length === 0) return prev;
            const added = toAdd.map((pieceIndex) => {
                const home = homePosition(pieceIndex, grid);
                return {
                    id: newBasketBoardPieceId(pieceIndex),
                    pieceIndex,
                    x: home.x,
                    y: home.y,
                };
            });
            const merged = [...prev, ...added].map((b) => {
                const home = homePosition(b.pieceIndex, grid);
                return { ...b, x: home.x, y: home.y };
            });
            if (merged.length === 0) return merged;
            const { pieces } = tryAttachNeighbors(merged, merged[0]?.id, grid, cellPx);
            return pieces;
        });
        setDragging(null);
        setActiveGroupId(null);
    }, [cellPx, grid, pieceIds]);

    const resetBasket = useCallback(() => {
        const stamp = Date.now();
        setBoardPieces([]);
        setBasketScatterPositions(
            buildBasketTrayScatter(pieceIds, grid, `basket-tray-${basketIndex}-shuffle-${stamp}`),
        );
        setDragging(null);
        setActiveGroupId(null);
    }, [basketIndex, grid, pieceIds]);

    const resetAllPlayRecords = useCallback(async () => {
        if (playState === PLAY_STATE_DEPLOYED) {
            setSnackbar({
                open: true,
                severity: "warning",
                message: "Deployed basket session cannot be reset.",
            });
            return;
        }
        const stamp = Date.now();
        const freshTray = buildBasketTrayScatter(
            pieceIds,
            grid,
            `basket-tray-${basketIndex}-full-reset-${stamp}`,
        );
        const freshScatter = buildScatterPositions(
            puzzlePieceCount,
            grid,
            `main-tray-${jigsawId || "local"}-${stamp}`,
        );

        setBoardPieces([]);
        setBasketScatterPositions(freshTray);
        setMainBoardPieces([]);
        setScatterPositions(freshScatter);
        setDragging(null);
        setActiveGroupId(null);
        setBoardZoom(1);
        setPlayState(PLAY_STATE_OPENED);

        const notes = [];
        if (jigsawId && resolvedBasketCount >= 1 && basketIndex != null) {
            const basketResult = await JigsawColorBasketPlayService.reset({
                jigsawId,
                basketCount: resolvedBasketCount,
                basketIndex,
                boardPositions: [],
            });
            if (basketResult.success) {
                persistReadyRef.current = true;
                const state = basketResult.data?.playState;
                setPlayState(state === PLAY_STATE_DEPLOYED ? PLAY_STATE_DEPLOYED : PLAY_STATE_OPENED);
                notes.push("basket play");
            } else {
                notes.push(`basket play failed (${basketResult.error || "error"})`);
            }

            const scatterPayload = {};
            for (let i = 0; i < puzzlePieceCount; i += 1) {
                scatterPayload[i] = freshScatter[i] || { x: 0, y: 0 };
            }
            const mainResult = await JigsawPlayService.progress({
                jigsawId,
                boardPositions: [],
                scatteredPieces: scatterPayload,
            });
            if (mainResult.success) {
                notes.push("main board play");
            } else {
                const started = await JigsawPlayService.start({
                    jigsawId,
                    scatteredPieces: scatterPayload,
                    boardPositions: [],
                });
                if (started.success) {
                    const cleared = await JigsawPlayService.progress({
                        jigsawId,
                        boardPositions: [],
                        scatteredPieces: scatterPayload,
                    });
                    notes.push(
                        cleared.success
                            ? "main board play"
                            : `main board failed (${cleared.error || started.error || "error"})`,
                    );
                } else {
                    notes.push(`main board failed (${started.error || "error"})`);
                }
            }
        }

        setSnackbar({
            open: true,
            severity: "success",
            message:
                notes.length > 0
                    ? `Reset local boards. Synced: ${notes.join(", ")}.`
                    : "Reset basket board and main board (local).",
        });
    }, [
        basketIndex,
        grid,
        jigsawId,
        pieceIds,
        playState,
        puzzlePieceCount,
        resolvedBasketCount,
    ]);

    const placeGroupOnMainBoard = useCallback(async () => {
        if (!placeableGroup?.members?.length) return;
        setPlaceBusy(true);

        const stamp = Date.now();
        const transferGroupId = placeableGroup.groupId || newPieceGroupId();
        const memberIndexes = placeableGroup.members.map((m) => m.pieceIndex);
        // Lock clued pieces onto the main board at their home cells.
        const toPlace = placeableGroup.members.map((m, i) => {
            const home = homePosition(m.pieceIndex, grid);
            return {
                id: `main-${m.pieceIndex}-${stamp}-${i}`,
                pieceIndex: m.pieceIndex,
                x: home.x,
                y: home.y,
                groupId: transferGroupId,
            };
        });
        const placedIndexes = new Set(memberIndexes);

        setMainBoardPieces((prev) => {
            const kept = prev.filter((b) => !placedIndexes.has(b.pieceIndex));
            return [...kept, ...toPlace];
        });
        setScatterPositions((prev) => {
            const next = { ...prev };
            placedIndexes.forEach((pi) => {
                delete next[pi];
            });
            return next;
        });
        setBoardPieces((prev) => {
            const next = prev.filter((b) => !placedIndexes.has(b.pieceIndex));
            if (next.length === 0) {
                setPlayState(PLAY_STATE_DEPLOYED);
            }
            return next;
        });
        setActiveGroupId(null);

        let apiNote = "";
        if (jigsawId) {
            // Basket deploy/place reads DB board layout — flush local basket first.
            if (persistReadyRef.current) {
                if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
                await saveProgress(boardPiecesRef.current, false);
            }
            const mainSession = await ensureMainPlaySession();
            if (!mainSession.success) {
                apiNote = ` (server sync skipped: ${mainSession.error || "no main play session"})`;
            } else {
                const result = await JigsawColorBasketPlayService.placeGroup({
                    jigsawId,
                    basketCount: resolvedBasketCount,
                    basketIndex,
                    groupId: placeableGroup.groupId,
                    pieceIndexes: memberIndexes,
                });
                if (!result.success) {
                    apiNote = ` (server sync skipped: ${result.error || "place-group failed"})`;
                } else {
                    if (result.data?.basketPlay?.playState === PLAY_STATE_DEPLOYED) {
                        setPlayState(PLAY_STATE_DEPLOYED);
                    }
                    const serverMain = extractMainBoardPositions(result.data);
                    if (serverMain) {
                        const fromServer = normalizeBoardPieceList(serverMain, "main");
                        if (fromServer.length > 0) setMainBoardPieces(fromServer);
                    }
                    onMainBoardSynced?.(result.data);
                }
            }
        }

        setPlaceBusy(false);
        setSnackbar({
            open: true,
            severity: "success",
            message: `Placed ${toPlace.length} clued pieces on the main board at home.${apiNote}`,
        });
        if (!hideMainBoard) {
            requestAnimationFrame(() => {
                document.getElementById("admin-color-basket-main-board")?.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                });
            });
        }
    }, [
        basketIndex,
        ensureMainPlaySession,
        grid,
        hideMainBoard,
        jigsawId,
        onMainBoardSynced,
        placeableGroup,
        resolvedBasketCount,
        saveProgress,
    ]);

    const deployAllOnMainBoard = useCallback(async () => {
        if (!isSolved || boardPieces.length === 0 || deployBusy) return;
        setDeployBusy(true);

        const stamp = Date.now();
        // Snapshot resolved pieces for the post-deploy preview (board is cleared below).
        const resolvedSnapshot = boardPieces.map((m) => ({
            pieceIndex: m.pieceIndex,
            x: m.x,
            y: m.y,
            ...(m.groupId ? { groupId: m.groupId } : {}),
        }));
        // Keep glued layout (current x/y + groupId) — do not snap to home.
        const toPlace = resolvedSnapshot.map((m, i) => ({
            id: `main-${m.pieceIndex}-${stamp}-${i}`,
            pieceIndex: m.pieceIndex,
            x: m.x,
            y: m.y,
            ...(m.groupId ? { groupId: m.groupId } : {}),
        }));
        const placedIndexes = new Set(toPlace.map((p) => p.pieceIndex));
        const placedCount = toPlace.length;

        setMainBoardPieces((prev) => {
            const kept = prev.filter((b) => !placedIndexes.has(b.pieceIndex));
            return [...kept, ...toPlace];
        });
        setBoardPieces([]);
        setActiveGroupId(null);
        setPlayState(PLAY_STATE_DEPLOYED);

        let apiNote = "";
        if (jigsawId) {
            // Deploy copies basket DB positions onto main play — sync basket board first.
            if (persistReadyRef.current) {
                if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
                await saveProgress(toPlace.map((p) => ({
                    id: p.id,
                    pieceIndex: p.pieceIndex,
                    x: p.x,
                    y: p.y,
                    ...(p.groupId ? { groupId: p.groupId } : {}),
                })), false);
            }
            const mainSession = await ensureMainPlaySession();
            if (!mainSession.success) {
                apiNote = ` (server sync skipped: ${mainSession.error || "no main play session"})`;
            } else {
                const result = await JigsawColorBasketPlayService.deploy({
                    jigsawId,
                    basketCount: resolvedBasketCount,
                    basketIndex,
                });
                if (!result.success) {
                    apiNote = ` (server sync skipped: ${result.error || "deploy failed"})`;
                } else {
                    if (result.data?.basketPlay?.playState) {
                        setPlayState(
                            result.data.basketPlay.playState === PLAY_STATE_DEPLOYED
                                ? PLAY_STATE_DEPLOYED
                                : PLAY_STATE_OPENED,
                        );
                    }
                    const serverMain = extractMainBoardPositions(result.data);
                    if (serverMain) {
                        setMainBoardPieces(normalizeBoardPieceList(serverMain, "main"));
                    }
                    // Persist empty basket board after successful deploy.
                    if (persistReadyRef.current) {
                        await saveProgress([], true);
                    }
                    onMainBoardSynced?.(result.data);
                }
            }
        }

        setDeployBusy(false);
        const deployMessage = apiNote
            ? `These ${placedCount} resolved pieces are on the main puzzle.${apiNote}`
            : `These ${placedCount} resolved pieces are now on the main puzzle.`;
        if (simpleMode) {
            setDeployDoneDialog({
                open: true,
                message: deployMessage,
                pieces: resolvedSnapshot,
                placedCount,
            });
        } else {
            setSnackbar({
                open: true,
                severity: apiNote ? "warning" : "success",
                message: deployMessage,
            });
        }
    }, [
        basketIndex,
        boardPieces,
        deployBusy,
        ensureMainPlaySession,
        isSolved,
        jigsawId,
        onMainBoardSynced,
        resolvedBasketCount,
        saveProgress,
        simpleMode,
    ]);

    const startDrag = useCallback(
        (e, pieceIndex, opts) => {
            e.preventDefault();
            e.stopPropagation();
            const touch = e.touches ? e.touches[0] : e;
            const clientX = touch.clientX;
            const clientY = touch.clientY;
            let grabDx = cellPx / 2;
            let grabDy = cellPx / 2;
            let ghostScale = 1;
            const elRect = e.currentTarget.getBoundingClientRect();
            const boardId = opts.boardId ?? null;
            const groupId = opts.groupId ?? null;
            const boardX = opts.boardX ?? 0;
            const boardY = opts.boardY ?? 0;
            const fromScattered = !!opts.fromScattered;
            const listTray = !!opts.listTray || fromScattered;
            const boardLane = opts.boardLane === "main" ? "main" : "basket";
            const activeBoardEl =
                boardLane === "main" ? mainBoardRef.current : boardRef.current;
            const lanePiecesRef =
                boardLane === "main" ? mainBoardPiecesRef : boardPiecesRef;

            if (boardId) {
                const selected = lanePiecesRef.current.find((piece) => piece.id === boardId);
                const selectedGroup = selected?.groupId
                    ? lanePiecesRef.current.filter(
                          (piece) => piece.groupId === selected.groupId,
                      )
                    : selected
                      ? [selected]
                      : [];
                if (
                    selectedGroup.some((piece) =>
                        isPieceExactlyAtHome(piece, grid, cellPx),
                    )
                ) {
                    return;
                }
            }

            if (fromScattered) {
                // Tray list pieces are CSS-scaled; keep ghost at tray size and map grab into
                // board cell space (visual includes tab margin around the cell).
                const nativeRender = pieceVisualRenderSizePx(cellPx) || cellPx;
                const tabOffset = pieceVisualCellOffsetPx(cellPx);
                ghostScale = elRect.width > 0 ? elRect.width / nativeRender : 1;
                if (elRect.width > 0 && elRect.height > 0) {
                    grabDx =
                        ((clientX - elRect.left) / elRect.width) * nativeRender - tabOffset;
                    grabDy =
                        ((clientY - elRect.top) / elRect.height) * nativeRender - tabOffset;
                } else {
                    grabDx = cellPx / 2;
                    grabDy = cellPx / 2;
                }
            } else if (activeBoardEl && boardId) {
                const rect = activeBoardEl.getBoundingClientRect();
                const s = rect.width / BOARD_SIZE || 1;
                ghostScale = s;
                grabDx = (clientX - rect.left) / s - boardX;
                grabDy = (clientY - rect.top) / s - boardY;
            }

            let groupDragMembers = null;
            if (groupId && boardId) {
                const members = lanePiecesRef.current.filter((b) => b.groupId === groupId);
                if (members.length > 1) {
                    groupDragMembers = members.map((member) => ({
                        pieceIndex: member.pieceIndex,
                        dx: member.x - boardX,
                        dy: member.y - boardY,
                    }));
                }
            }

            if (groupId && boardLane === "basket") setActiveGroupId(groupId);

            try {
                e.currentTarget?.setPointerCapture?.(e.pointerId);
            } catch {
                /* ignore — not all events are PointerEvents */
            }

            setDragging({
                pieceIndex,
                boardId,
                groupId,
                groupDragMembers,
                fromScattered,
                listTray,
                boardLane,
                grabDx,
                grabDy,
                ghostScale,
                startX: clientX,
                startY: clientY,
                elLeft: elRect.left,
                elTop: elRect.top,
                elWidth: elRect.width,
                elHeight: elRect.height,
            });
        },
        [cellPx, grid],
    );

    useEffect(() => {
        if (!dragging) return undefined;

        const ghost = dragGhostRef.current;
        const scale = dragging.ghostScale || 1;
        if (ghost) {
            ghost.style.left = `${dragging.elLeft}px`;
            ghost.style.top = `${dragging.elTop}px`;
            ghost.style.transform = `scale(${scale})`;
        }

        let settled = false;

        const onMove = (e) => {
            const touch = e.touches ? e.touches[0] : e;
            const deltaX = touch.clientX - dragging.startX;
            const deltaY = touch.clientY - dragging.startY;
            if (dragGhostRef.current) {
                dragGhostRef.current.style.transform =
                    `translate3d(${deltaX}px, ${deltaY}px, 0) scale(${scale})`;
            }
            if (e.cancelable) e.preventDefault();
        };

        const onUp = (e) => {
            if (settled) return;
            settled = true;
            const touch = e.changedTouches ? e.changedTouches[0] : e;
            const cx = touch?.clientX ?? e.clientX;
            const cy = touch?.clientY ?? e.clientY;
            if (!Number.isFinite(cx) || !Number.isFinite(cy)) {
                setDragging(null);
                return;
            }
            const {
                grabDx,
                grabDy,
                boardId,
                fromScattered,
                listTray,
                pieceIndex,
                boardLane = "basket",
            } = dragging;
            const isMainLane = boardLane === "main";
            const basketHitEl = boardViewportRef.current || boardRef.current;
            const mainHitEl = mainBoardRef.current;
            const hitPad = Math.max(
                24,
                ((isMainLane ? mainHitEl : basketHitEl)?.getBoundingClientRect().width || 0) * 0.04,
            );
            const onBasketBoard = basketHitEl && pointInRect(cx, cy, basketHitEl, hitPad);
            const onMainBoard = mainHitEl && pointInRect(cx, cy, mainHitEl, hitPad);
            const onBasketTray =
                basketTrayRef.current && pointInRect(cx, cy, basketTrayRef.current, hitPad);
            const onMainTray =
                scatteredRef.current && pointInRect(cx, cy, scatteredRef.current, hitPad);

            const resolveDropLocal = (boardEl, useCrop) => {
                const tabMargin = cellPx * 0.35;
                let nx;
                let ny;
                if (fromScattered && listTray && dragging.elWidth > 0 && boardEl) {
                    const circleLeft = dragging.elLeft + (cx - dragging.startX);
                    const circleTop = dragging.elTop + (cy - dragging.startY);
                    const fromCircle = listTrayDropCellOrigin(
                        circleLeft,
                        circleTop,
                        dragging.elWidth,
                        boardEl,
                        cellPx,
                    );
                    if (fromCircle) {
                        nx = fromCircle.x;
                        ny = fromCircle.y;
                    }
                }
                    if (!Number.isFinite(nx) || !Number.isFinite(ny)) {
                    let local = null;
                    if (!fromScattered && boardEl) {
                        local = boardLocalFromClient(cx, cy, boardEl, grabDx, grabDy);
                    } else if (
                        useCrop
                        && basketCropBoundsPx
                        && boardViewportRef.current
                    ) {
                        local = boardLocalFromCropViewport(
                            cx,
                            cy,
                            boardViewportRef.current,
                            {
                                left: basketCropBoundsPx.left,
                                top: basketCropBoundsPx.top,
                                width: basketCropBoundsPx.width,
                                height: basketCropBoundsPx.height,
                            },
                            grabDx,
                            grabDy,
                        );
                    }
                    if (!local && boardEl) {
                        local = boardLocalFromClient(cx, cy, boardEl, grabDx, grabDy);
                    }
                    if (!local) return null;
                    nx = local.x;
                    ny = local.y;
                }
                if (useCrop && basketCropBoundsPx) {
                    nx = Math.max(
                        basketCropBoundsPx.left - tabMargin,
                        Math.min(basketCropBoundsPx.right - cellPx + tabMargin, nx),
                    );
                    ny = Math.max(
                        basketCropBoundsPx.top - tabMargin,
                        Math.min(basketCropBoundsPx.bottom - cellPx + tabMargin, ny),
                    );
                } else {
                    nx = Math.max(-tabMargin, Math.min(BOARD_SIZE - cellPx + tabMargin, nx));
                    ny = Math.max(-tabMargin, Math.min(BOARD_SIZE - cellPx + tabMargin, ny));
                }
                return { nx, ny };
            };

            const placeOrMoveOnLane = (lane, boardEl, useCrop) => {
                const pos = resolveDropLocal(boardEl, useCrop);
                if (!pos) return false;
                const { nx, ny } = pos;
                const setLanePieces = lane === "main" ? setMainBoardPieces : setBoardPieces;
                if (fromScattered) {
                    const newPiece = {
                        id:
                            lane === "main"
                                ? `main-${pieceIndex}-${Date.now().toString(36)}`
                                : newBasketBoardPieceId(pieceIndex),
                        pieceIndex,
                        x: nx,
                        y: ny,
                    };
                    const previousLane =
                        lane === "main"
                            ? mainBoardPiecesRef.current
                            : boardPiecesRef.current;
                    const without = previousLane.filter((b) => b.pieceIndex !== pieceIndex);
                    const { pieces: placed } = placeBoardPieceWithAttach(
                        without,
                        newPiece,
                        grid,
                        cellPx,
                    );
                    const next = snapMovedPiecesToHome(placed, grid, cellPx);
                    setLanePieces(next);
                    if (lane === "basket") {
                        const movedPiece = next.find((b) => b.pieceIndex === pieceIndex);
                        if (movedPiece?.groupId) setActiveGroupId(movedPiece.groupId);
                        celebrateHomePlacement(without, next);
                        flashGluedPieces(without, next);
                    }
                    if (lane === "main") {
                        // Piece now lives on main — remove from basket board if present.
                        setBoardPieces((prev) => prev.filter((b) => b.pieceIndex !== pieceIndex));
                    }
                    return true;
                }
                if (boardId) {
                    const previousLane =
                        lane === "main"
                            ? mainBoardPiecesRef.current
                            : boardPiecesRef.current;
                    const { pieces: moved } = moveBoardPieceGroup(
                        previousLane,
                        boardId,
                        nx,
                        ny,
                        grid,
                        cellPx,
                        { snapHome: false },
                    );
                    const { pieces: attachedPieces } = tryAttachNeighbors(
                        moved,
                        boardId,
                        grid,
                        cellPx,
                    );
                    const next = snapMovedPiecesToHome(attachedPieces, grid, cellPx);
                    setLanePieces(next);
                    if (lane === "basket") {
                        const movedPiece = next.find((b) => b.id === boardId);
                        if (movedPiece?.groupId) setActiveGroupId(movedPiece.groupId);
                        celebrateHomePlacement(previousLane, next);
                        flashGluedPieces(previousLane, next);
                    }
                    return true;
                }
                return false;
            };

            const returnToTray = (trayEl, setScatter, lanePiecesRef, setLanePieces) => {
                if (!trayEl) return;
                const local = boardLocalFromClient(cx, cy, trayEl, grabDx, grabDy);
                const maxX = Math.max(0, BOARD_SIZE - cellPx);
                const maxY = Math.max(0, BOARD_SIZE - cellPx);
                const nx = Math.max(0, Math.min(maxX, local.x));
                const ny = Math.max(0, Math.min(maxY, local.y));

                if (boardId && !fromScattered) {
                    const members = lanePiecesRef.current.filter((b) => {
                        if (dragging.groupId) return b.groupId === dragging.groupId;
                        return b.id === boardId;
                    });
                    const toRemove = members.length > 0
                        ? members
                        : lanePiecesRef.current.filter((b) => b.id === boardId);
                    const removeIds = new Set(toRemove.map((b) => b.pieceIndex));
                    setLanePieces((prev) => prev.filter((b) => !removeIds.has(b.pieceIndex)));
                    setScatter((prev) => {
                        const next = { ...prev };
                        let i = 0;
                        removeIds.forEach((pi) => {
                            next[pi] = {
                                x: Math.max(0, Math.min(maxX, nx + (i % 3) * cellPx * 0.15)),
                                y: Math.max(0, Math.min(maxY, ny + Math.floor(i / 3) * cellPx * 0.15)),
                            };
                            i += 1;
                        });
                        return next;
                    });
                    if (!isMainLane) setActiveGroupId(null);
                } else if (fromScattered) {
                    setScatter((prev) => ({
                        ...prev,
                        [pieceIndex]: { x: nx, y: ny },
                    }));
                }
            };

            // Prefer the lane the drag started from (avoids overlapping hit targets).
            if (isMainLane) {
                if (onMainBoard && mainBoardRef.current) {
                    placeOrMoveOnLane("main", mainBoardRef.current, false);
                    setDragging(null);
                    return;
                }
                if (onMainTray) {
                    returnToTray(
                        scatteredRef.current,
                        setScatterPositions,
                        mainBoardPiecesRef,
                        setMainBoardPieces,
                    );
                    setDragging(null);
                    return;
                }
            } else {
                if (onBasketBoard && (boardRef.current || boardViewportRef.current)) {
                    placeOrMoveOnLane("basket", boardRef.current, true);
                    setDragging(null);
                    return;
                }
                if (onBasketTray) {
                    returnToTray(
                        basketTrayRef.current,
                        setBasketScatterPositions,
                        boardPiecesRef,
                        setBoardPieces,
                    );
                    setDragging(null);
                    return;
                }
                // Allow main-tray → main-board when a basket-tray drag lands on main.
                if (fromScattered && onMainBoard && mainBoardRef.current) {
                    placeOrMoveOnLane("main", mainBoardRef.current, false);
                    setDragging(null);
                    return;
                }
            }

            setDragging(null);
        };

        window.addEventListener("pointermove", onMove, { passive: false });
        window.addEventListener("pointerup", onUp);
        window.addEventListener("pointercancel", onUp);
        // Touch fallback when PointerEvent is unavailable (rare).
        if (typeof window.PointerEvent === "undefined") {
            window.addEventListener("touchmove", onMove, { passive: false });
            window.addEventListener("touchend", onUp);
            window.addEventListener("touchcancel", onUp);
        }
        return () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
            window.removeEventListener("pointercancel", onUp);
            window.removeEventListener("touchmove", onMove);
            window.removeEventListener("touchend", onUp);
            window.removeEventListener("touchcancel", onUp);
        };
    }, [
        cellPx,
        dragging,
        grid,
        basketCropBoundsPx,
        celebrateHomePlacement,
        flashGluedPieces,
    ]);

    const zoomOut = useCallback(() => {
        setBoardZoom((z) =>
            Math.max(BOARD_ZOOM_MIN, Math.round((z - BOARD_ZOOM_STEP) * 100) / 100),
        );
    }, []);

    const zoomIn = useCallback(() => {
        setBoardZoom((z) =>
            Math.min(BOARD_ZOOM_MAX, Math.round((z + BOARD_ZOOM_STEP) * 100) / 100),
        );
    }, []);

    const zoomReset = useCallback(() => setBoardZoom(1), []);

    /** Keep the zoomed board centered in its slot after each zoom step. */
    useEffect(() => {
        const el = boardScrollRef.current;
        if (!el) return undefined;
        const center = () => {
            el.scrollLeft = Math.max(0, (el.scrollWidth - el.clientWidth) / 2);
            el.scrollTop = Math.max(0, (el.scrollHeight - el.clientHeight) / 2);
        };
        center();
        // The board canvas remounts on zoom; re-center once it has laid out.
        const raf = window.requestAnimationFrame(center);
        return () => window.cancelAnimationFrame(raf);
    }, [boardZoom]);

    /** Drag-to-pan the zoomed board; a piece grab stops propagation, so it wins over panning. */
    const handleBoardPanPointerDown = useCallback(
        (e) => {
            const el = boardScrollRef.current;
            if (!el || boardZoom <= 1 || e.button !== 0) return;
            boardPanRef.current = {
                pointerId: e.pointerId,
                startX: e.clientX,
                startY: e.clientY,
                startScrollLeft: el.scrollLeft,
                startScrollTop: el.scrollTop,
                active: false,
            };
        },
        [boardZoom],
    );

    const handleBoardPanPointerMove = useCallback((e) => {
        const el = boardScrollRef.current;
        const pan = boardPanRef.current;
        if (!el || !pan || pan.pointerId !== e.pointerId) return;
        const dx = e.clientX - pan.startX;
        const dy = e.clientY - pan.startY;
        if (!pan.active) {
            if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
            pan.active = true;
            el.style.cursor = "grabbing";
            try {
                el.setPointerCapture(e.pointerId);
            } catch {
                /* ignore */
            }
        }
        if (e.cancelable) e.preventDefault();
        el.scrollLeft = pan.startScrollLeft - dx;
        el.scrollTop = pan.startScrollTop - dy;
    }, []);

    const handleBoardPanPointerUp = useCallback((e) => {
        const el = boardScrollRef.current;
        const pan = boardPanRef.current;
        if (!pan || pan.pointerId !== e.pointerId) return;
        if (el) {
            el.style.cursor = "";
            try {
                el.releasePointerCapture(e.pointerId);
            } catch {
                /* already released */
            }
        }
        boardPanRef.current = null;
    }, []);

    const saveLabel =
        saveStatus === "saving"
            ? "Saving…"
            : saveStatus === "saved"
              ? "Saved"
              : saveStatus === "error"
                ? "Save failed"
                : jigsawId
                  ? "Autosave on"
                  : null;

    const renderBoardToolbar = () => {
        if (simpleMode && !promoCapture) {
            const playIconSx = PLAY_TOOLBAR_ICON_BTN_SX;
            return (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        width: "100%",
                        minWidth: 0,
                        flexShrink: 0,
                        mb: 1.5,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            minWidth: 0,
                            flex: "1 1 auto",
                            pr: 1,
                        }}
                    >
                        {toolbarLeading}
                        {puzzleTitle ? (
                            <Typography
                                variant="subtitle1"
                                component="h1"
                                noWrap
                                title={puzzleTitle}
                                sx={{
                                    fontWeight: 700,
                                    color: PLAY_TOOLBAR_FG,
                                    minWidth: 0,
                                    lineHeight: 1.2,
                                    fontSize: { xs: "1.05rem", sm: "1.15rem" },
                                }}
                            >
                                {puzzleTitle}
                            </Typography>
                        ) : null}
                    </Box>
                    <PlayToolbarScrollRow sx={PLAY_TOOLBAR_PILL_SX}>
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.65,
                                px: 0.85,
                                py: 0.35,
                                borderRadius: 999,
                                bgcolor: "rgba(27, 67, 50, 0.08)",
                                border: "1px solid rgba(27, 67, 50, 0.16)",
                                flexShrink: 0,
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 800,
                                    color: PLAY_TOOLBAR_FG,
                                    whiteSpace: "nowrap",
                                    fontSize: "0.75rem",
                                }}
                            >
                                Basket
                            </Typography>
                            <Box
                                aria-hidden
                                sx={{
                                    ...colorBasketNumberBadgeSx({ size: 20 }),
                                    flexShrink: 0,
                                }}
                            >
                                {Number(basketIndex) + 1}
                            </Box>
                            <Box
                                aria-hidden
                                title={hex}
                                sx={{
                                    width: 18,
                                    height: 18,
                                    minWidth: 18,
                                    borderRadius: 0.75,
                                    bgcolor: hex,
                                    border: "1.5px solid rgba(255,255,255,0.85)",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.28)",
                                    flexShrink: 0,
                                    boxSizing: "border-box",
                                }}
                            />
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 700,
                                    color: PLAY_TOOLBAR_FG,
                                    whiteSpace: "nowrap",
                                    fontSize: "0.75rem",
                                }}
                            >
                                {basketTotal}
                            </Typography>
                        </Box>
                        {toolbarMid}
                        <Tooltip title="Piece map">
                            <IconButton
                                size="medium"
                                aria-label="Open piece map"
                                onClick={() => setMiniMapDialogOpen(true)}
                                sx={playIconSx}
                            >
                                <MapOutlinedIcon fontSize="medium" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Zoom out">
                            <span>
                                <IconButton
                                    size="medium"
                                    aria-label="Zoom out basket board"
                                    onClick={zoomOut}
                                    disabled={boardZoom <= BOARD_ZOOM_MIN}
                                    sx={playIconSx}
                                >
                                    <RemoveIcon fontSize="medium" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Typography
                            variant="caption"
                            sx={{
                                minWidth: 36,
                                textAlign: "center",
                                fontWeight: 700,
                                fontSize: "0.85rem",
                                color: PLAY_TOOLBAR_FG,
                                flexShrink: 0,
                            }}
                        >
                            {Math.round(boardZoom * 100)}%
                        </Typography>
                        <Tooltip title="Zoom in">
                            <span>
                                <IconButton
                                    size="medium"
                                    aria-label="Zoom in basket board"
                                    onClick={zoomIn}
                                    disabled={boardZoom >= BOARD_ZOOM_MAX}
                                    sx={playIconSx}
                                >
                                    <AddIcon fontSize="medium" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        {isAdminRole ? (
                            <Tooltip title="Place all tray pieces on the board at home">
                                <span>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={placeAllFitOnBoard}
                                        disabled={piecesInBasketTray.length === 0}
                                        sx={{
                                            minWidth: 0,
                                            px: 1.25,
                                            color: PLAY_TOOLBAR_FG,
                                            borderColor: "rgba(27, 67, 50, 0.35)",
                                        }}
                                    >
                                        All-fit
                                    </Button>
                                </span>
                            </Tooltip>
                        ) : null}
                        <Tooltip
                            title={
                                isSolved
                                    ? "Deploy collected pieces onto the main board"
                                    : "Finish the basket board before deploying"
                            }
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    onClick={deployAllOnMainBoard}
                                    disabled={!isSolved || deployBusy || placeBusy}
                                    startIcon={<RocketLaunchIcon />}
                                    aria-label="Deploy collected pieces onto the main board"
                                    sx={{
                                        ml: 0.25,
                                        flexShrink: 0,
                                        fontWeight: 800,
                                        textTransform: "none",
                                        bgcolor: "#3BA666",
                                        color: "#fff",
                                        minHeight: 40,
                                        px: 1.75,
                                        fontSize: "0.9rem",
                                        "&:hover": { bgcolor: "#2e7d32" },
                                        "&.Mui-disabled": {
                                            bgcolor: "rgba(59, 166, 102, 0.35)",
                                            color: "rgba(255,255,255,0.75)",
                                        },
                                        "& .MuiSvgIcon-root": { fontSize: "1.25rem" },
                                    }}
                                >
                                    Deploy
                                </Button>
                            </span>
                        </Tooltip>
                        {toolbarEnd}
                    </PlayToolbarScrollRow>
                </Box>
            );
        }

        return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ flexWrap: "wrap", width: "100%" }}
            useFlexGap
        >
            {!simpleMode ? (
                <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1, minWidth: 140 }}>
                    Basket board ({homeCount}/{basketTotal})
                    {piecesInBasketTray.length > 0
                        ? ` · ${piecesInBasketTray.length} in tray`
                        : ""}
                </Typography>
            ) : (
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ flex: 1, minWidth: 0, flexWrap: "wrap" }}
                    useFlexGap
                >
                    {toolbarLeading}
                    {puzzleTitle ? (
                        <Typography
                            variant={promoCapture ? "h6" : "body2"}
                            noWrap
                            title={puzzleTitle}
                            sx={{
                                fontWeight: promoCapture ? 800 : 700,
                                color: "#3BA666",
                                maxWidth: promoCapture
                                    ? { xs: 180, sm: 320, md: 480 }
                                    : { xs: 120, sm: 200, md: 280 },
                                lineHeight: 1.2,
                                fontSize: promoCapture ? "1.75rem" : undefined,
                            }}
                        >
                            {puzzleTitle}
                        </Typography>
                    ) : null}
                    <Box
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: promoCapture ? 1.5 : 0.75,
                            px: promoCapture ? 2 : 1,
                            py: promoCapture ? 1.1 : 0.4,
                            borderRadius: 1.5,
                            bgcolor: "rgba(255, 244, 230, 0.95)",
                            border: "1px solid",
                            borderColor: "rgba(196, 140, 60, 0.35)",
                            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.65)",
                            flexShrink: 0,
                        }}
                    >
                        <Typography
                            variant={promoCapture ? "subtitle1" : "body2"}
                            sx={{
                                fontWeight: promoCapture ? 800 : 700,
                                color: "text.primary",
                                whiteSpace: "nowrap",
                                fontSize: promoCapture ? "1.5rem" : undefined,
                            }}
                        >
                            Basket
                        </Typography>
                        <Box
                            aria-hidden
                            sx={{
                                ...colorBasketNumberBadgeSx({ size: promoCapture ? 48 : 22 }),
                                flexShrink: 0,
                            }}
                        >
                            {Number(basketIndex) + 1}
                        </Box>
                        <Box
                            aria-hidden
                            title={hex}
                            sx={{
                                width: promoCapture ? 48 : 22,
                                height: promoCapture ? 48 : 22,
                                minWidth: promoCapture ? 48 : 22,
                                borderRadius: 0.75,
                                bgcolor: hex,
                                border: "1.5px solid rgba(255,255,255,0.85)",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.28)",
                                flexShrink: 0,
                                boxSizing: "border-box",
                            }}
                        />
                        <Typography
                            variant={promoCapture ? "subtitle1" : "body2"}
                            sx={{
                                fontWeight: promoCapture ? 800 : 700,
                                color: "text.primary",
                                whiteSpace: "nowrap",
                                minWidth: 0,
                                fontSize: promoCapture ? "1.5rem" : undefined,
                            }}
                        >
                            {basketTotal} {basketTotal === 1 ? "piece" : "pieces"}
                        </Typography>
                    </Box>
                    {toolbarMid}
                </Stack>
            )}
            {!simpleMode && isSolved ? (
                <Typography variant="subtitle2" color="success.main" sx={{ fontWeight: 700 }}>
                    Solved!
                </Typography>
            ) : null}
            {!simpleMode && saveLabel ? (
                <Typography
                    variant="caption"
                    color={saveStatus === "error" ? "error.main" : "text.secondary"}
                >
                    {saveLabel}
                </Typography>
            ) : null}
            {simpleMode ? (
                <Tooltip title="Piece map">
                    <IconButton
                        size={promoCapture ? "medium" : "small"}
                        aria-label="Open piece map"
                        onClick={() => setMiniMapDialogOpen(true)}
                        sx={promoCapture ? PROMO_TOOLBAR_ICON_SX : { color: "#3BA666" }}
                    >
                        <MapOutlinedIcon fontSize={promoCapture ? "medium" : "small"} />
                    </IconButton>
                </Tooltip>
            ) : null}
            <Tooltip title="Zoom out">
                <span>
                    <IconButton
                        size={promoCapture ? "medium" : "small"}
                        aria-label="Zoom out basket board"
                        onClick={zoomOut}
                        disabled={boardZoom <= BOARD_ZOOM_MIN}
                        sx={promoCapture ? PROMO_TOOLBAR_ICON_SX : undefined}
                    >
                        <RemoveIcon fontSize={promoCapture ? "medium" : "small"} />
                    </IconButton>
                </span>
            </Tooltip>
            <Typography
                variant={promoCapture ? "h6" : "caption"}
                color="text.secondary"
                sx={{
                    minWidth: promoCapture ? 72 : 36,
                    textAlign: "center",
                    fontWeight: promoCapture ? 800 : undefined,
                    fontSize: promoCapture ? "1.5rem" : undefined,
                }}
            >
                {Math.round(boardZoom * 100)}%
            </Typography>
            <Tooltip title="Zoom in">
                <span>
                    <IconButton
                        size={promoCapture ? "medium" : "small"}
                        aria-label="Zoom in basket board"
                        onClick={zoomIn}
                        disabled={boardZoom >= BOARD_ZOOM_MAX}
                        sx={promoCapture ? PROMO_TOOLBAR_ICON_SX : undefined}
                    >
                        <AddIcon fontSize={promoCapture ? "medium" : "small"} />
                    </IconButton>
                </span>
            </Tooltip>
            {!simpleMode ? (
                <Tooltip title="Reset zoom">
                    <span>
                        <IconButton
                            size="small"
                            aria-label="Reset basket board zoom"
                            onClick={zoomReset}
                            disabled={boardZoom === 1}
                        >
                            <RestartAltIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            ) : null}
            {!simpleMode ? (
                <Tooltip title="Place all tray pieces on the board at random">
                    <span>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={placeAllOnBoard}
                            disabled={piecesInBasketTray.length === 0}
                            sx={{ ml: 0.5, minWidth: 0, px: 1.25 }}
                        >
                            All
                        </Button>
                    </span>
                </Tooltip>
            ) : null}
            {!simpleMode || isAdminRole ? (
                <Tooltip title="Place all tray pieces on the board at home">
                    <span>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={placeAllFitOnBoard}
                            disabled={piecesInBasketTray.length === 0}
                            sx={{ minWidth: 0, px: 1.25 }}
                        >
                            All-fit
                        </Button>
                    </span>
                </Tooltip>
            ) : null}
            {!simpleMode ? (
                <Tooltip
                    title={
                        playState === PLAY_STATE_DEPLOYED
                            ? "Deployed basket cannot be reset"
                            : "Reset basket play and main board play records"
                    }
                >
                    <span>
                        <IconButton
                            size="small"
                            color="warning"
                            aria-label="Reset basket play and main board play records"
                            onClick={resetAllPlayRecords}
                            disabled={playState === PLAY_STATE_DEPLOYED}
                            sx={{ ml: 0.5 }}
                        >
                            <ReplayIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            ) : null}
            <Tooltip
                title={
                    isSolved
                        ? "Deploy collected pieces onto the main board"
                        : "Finish the basket board before deploying"
                }
            >
                <span>
                    {promoCapture || simpleMode ? (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={deployAllOnMainBoard}
                            disabled={!isSolved || deployBusy || placeBusy}
                            startIcon={<RocketLaunchIcon />}
                            aria-label="Deploy collected pieces onto the main board"
                            sx={{
                                ml: 0.5,
                                flexShrink: 0,
                                fontWeight: 800,
                                textTransform: "none",
                                bgcolor: "#3BA666",
                                color: "#fff",
                                "&:hover": { bgcolor: "#2e7d32" },
                                "&.Mui-disabled": {
                                    bgcolor: "rgba(59, 166, 102, 0.35)",
                                    color: "rgba(255,255,255,0.75)",
                                },
                                ...(promoCapture
                                    ? {
                                          minHeight: 80,
                                          px: 3,
                                          py: 1.5,
                                          borderRadius: 2,
                                          fontSize: "1.35rem",
                                          "& .MuiButton-startIcon": { mr: 1.25 },
                                          "& .MuiSvgIcon-root": { fontSize: "2.4rem" },
                                      }
                                    : {
                                          minHeight: 44,
                                          px: 2,
                                          fontSize: "0.95rem",
                                          "& .MuiSvgIcon-root": { fontSize: "1.35rem" },
                                      }),
                            }}
                        >
                            Deploy
                        </Button>
                    ) : (
                        <IconButton
                            size="small"
                            color="primary"
                            aria-label="Deploy collected pieces onto the main board"
                            onClick={deployAllOnMainBoard}
                            disabled={!isSolved || deployBusy || placeBusy}
                        >
                            <RocketLaunchIcon fontSize="small" />
                        </IconButton>
                    )}
                </span>
            </Tooltip>
            {!simpleMode ? (
                boardFullscreen ? (
                    <Tooltip title="Close full screen">
                        <IconButton
                            size="small"
                            aria-label="Close full screen basket board"
                            onClick={() => setBoardFullscreen(false)}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Tooltip title="Open full screen">
                        <IconButton
                            size="small"
                            aria-label="Open basket board full screen"
                            onClick={() => setBoardFullscreen(true)}
                        >
                            <OpenInFullIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )
            ) : null}
            {simpleMode ? toolbarEnd : null}
        </Stack>
        );
    };

    const renderBoardCanvas = () => (
            <Box
                ref={boardScrollRef}
                onPointerDown={handleBoardPanPointerDown}
                onPointerMove={handleBoardPanPointerMove}
                onPointerUp={handleBoardPanPointerUp}
                onPointerCancel={handleBoardPanPointerUp}
                sx={{
                    width: "100%",
                    height: boardFullscreen || simpleMode ? "100%" : undefined,
                    overflow: boardFullscreen || simpleMode ? "auto" : "hidden",
                    ...((boardFullscreen || simpleMode) && boardZoom > 1
                        ? {
                              // Drag-to-pan owns the gesture; native touch scroll would double it.
                              touchAction: "none",
                              cursor: "grab",
                              scrollbarWidth: "none",
                              "&::-webkit-scrollbar": { display: "none" },
                          }
                        : null),
                    borderRadius: boardFullscreen ? 0 : 1,
                    border: boardFullscreen ? "none" : 1,
                    borderColor: "divider",
                    ...(simpleMode && !boardFullscreen
                        ? {
                              borderBottomLeftRadius: 0,
                              borderBottomRightRadius: 0,
                              borderBottom: "none",
                          }
                        : null),
                    bgcolor: boardFullscreen ? "background.default" : undefined,
                    flex: boardFullscreen || simpleMode ? 1 : undefined,
                    minHeight: boardFullscreen || simpleMode ? 0 : undefined,
                    position: boardFullscreen || simpleMode ? "relative" : undefined,
                }}
            >
            <Box
                sx={{
                    // Zoom grows the board; parent scrolls the overflow.
                    // Simple mode uses height+width so tall regions fit-to-height at 100%.
                    width:
                        boardFullscreen || simpleMode
                            ? `${boardZoom * 100}%`
                            : "100%",
                    height:
                        boardFullscreen || simpleMode
                            ? `${boardZoom * 100}%`
                            : undefined,
                    minWidth:
                        boardFullscreen || simpleMode
                            ? `${boardZoom * 100}%`
                            : undefined,
                    minHeight:
                        boardFullscreen || simpleMode ? "100%" : undefined,
                    display: boardFullscreen || simpleMode ? "flex" : undefined,
                    boxSizing: "border-box",
                }}
            >
                <CanvasBoard
                    key={simpleMode ? `basket-board-zoom-${boardZoom}` : "basket-board"}
                    boardRef={boardRef}
                    boardViewportRef={boardViewportRef}
                    boardPieces={boardPieces}
                    grid={grid}
                    imageUrl={imageUrl}
                    paths={paths}
                    svgPathFallback={svgPathFallback}
                    dragging={
                        dragging && dragging.boardLane !== "main"
                            ? {
                                  pieceIndex: dragging.pieceIndex,
                                  boardId: dragging.boardId,
                                  groupId: dragging.groupId,
                              }
                            : null
                    }
                    placedCount={homeCount}
                    total={basketTotal}
                    isSolved={isSolved}
                    onBoardPieceMouseDown={(e, pieceIndex, opts) =>
                        startDrag(e, pieceIndex, {
                            boardLane: "basket",
                            boardId: opts?.boardId,
                            boardX: opts?.boardX,
                            boardY: opts?.boardY,
                            groupId: opts?.groupId,
                        })
                    }
                    showPieceGridCoords={!simpleMode && !promoCapture}
                    showHeader={false}
                    readOnly={false}
                    lockHomePieces
                    activePieceShines={activePieceShines}
                    pieceGlueKeys={pieceGlueKeys}
                    showDieLines={false}
                    regionGuide={regionGuide}
                    cropToRegionGuide
                    cropPaddingCells={basketCropPadCells}
                    cropMaxHeight={simpleMode ? 0 : boardFullscreen ? 0 : 320}
                    matPreset={boardMat}
                    matColor={boardMat.hex}
                    grooveStyle={boardGrooveStyle}
                    fitParent={boardFullscreen || simpleMode}
                />
            </Box>
        </Box>
    );

    const renderBasketTray = () => {
        const nativeCell = grid > 0 ? BOARD_SIZE / grid : 24;
        const targetCell = boardFullscreen ? 56 : simpleMode ? 40 : 48;
        // Promo capture sizes pieces by tray thickness so the board keeps most of the frame.
        const listPieceScale = promoCapture
            ? Math.max(
                  0.8,
                  Math.min(
                      3.2,
                      PROMO_TRAY_ROW_PX / Math.max(pieceVisualRenderSizePx(nativeCell), 1),
                  ),
              )
            : Math.max(1.15, Math.min(2.75, targetCell / Math.max(nativeCell, 1)));

        return (
        <Box
            sx={{
                width: "100%",
                borderRadius: 1,
                ...(simpleMode
                    ? {
                          borderTopLeftRadius: 0,
                          borderTopRightRadius: 0,
                      }
                    : null),
                border: 1,
                borderColor: "divider",
                p: promoCapture ? 1.75 : simpleMode ? 0.75 : 1,
                bgcolor: "background.paper",
                flexShrink: 0,
                overflow: "visible",
            }}
        >
            <CanvasScattered
                scatteredRef={basketTrayRef}
                piecesNotOnBoard={piecesInBasketTray}
                scatterPositions={basketScatterPositions}
                grid={grid}
                imageUrl={imageUrl}
                paths={paths}
                dragging={
                    dragging?.fromScattered && dragging.boardLane !== "main"
                        ? {
                              pieceIndex: dragging.pieceIndex,
                              fromScattered: true,
                          }
                        : null
                }
                onScatteredPieceMouseDown={(e, pieceIndex, opts) =>
                    startDrag(e, pieceIndex, {
                        boardLane: "basket",
                        fromScattered: true,
                        listTray: true,
                        scatterX: opts?.scatterX,
                        scatterY: opts?.scatterY,
                    })
                }
                showPieceGridCoords={false}
                showViewToggle={!simpleMode}
                forceViewMode="list"
                listOrientation="row"
                listRows={promoCapture ? 1 : 2}
                listPieceScale={listPieceScale}
                trayDescription={
                    simpleMode
                        ? ""
                        : "Basket tray — 2 rows, scroll horizontally; drag pieces onto the board"
                }
            />
        </Box>
        );
    };

    if (!basket || !imageUrl || !paths || grid <= 0) {
        return null;
    }

    const groupMembers = dragging?.groupDragMembers;
    const isGroupGhost = Array.isArray(groupMembers) && groupMembers.length > 1;

    const simpleLayout = (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                minHeight: 0,
                p: 0,
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Box
                sx={{
                    mb: promoCapture ? 1.5 : 0,
                    flexShrink: 0,
                }}
            >
                {promoCapture ? (
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        borderRadius: 2,
                        bgcolor: "rgba(255,255,255,0.72)",
                        border: "1px solid",
                        borderColor: "rgba(59, 110, 150, 0.22)",
                        boxShadow: "0 2px 10px rgba(30, 70, 110, 0.08)",
                        "& .MuiStack-root": { spacing: 1.5, gap: 1.5 },
                    }}
                >
                    {renderBoardToolbar()}
                </Box>
                ) : (
                    renderBoardToolbar()
                )}
                {!promoCapture ? (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    component="div"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.25,
                        mb: 1,
                        px: 0.5,
                        lineHeight: 1.35,
                        flexWrap: "wrap",
                    }}
                >
                    <Box component="span">
                        Place every piece, then tap
                    </Box>
                    <RocketLaunchIcon
                        sx={{ fontSize: 16, color: "primary.main", flexShrink: 0 }}
                        aria-hidden
                    />
                    <Box component="span">to send them to the main board.</Box>
                </Typography>
                ) : null}
            </Box>
            <Box
                sx={{
                    width: "100%",
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0,
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        flex: "1 1 0",
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    }}
                >
                    {renderBoardCanvas()}
                </Box>
                <Box sx={{ flex: "0 0 auto", width: "100%", minHeight: 0 }}>
                    {renderBasketTray()}
                </Box>
            </Box>
            <Dialog
                open={miniMapDialogOpen}
                onClose={() => setMiniMapDialogOpen(false)}
                fullWidth
                maxWidth={false}
                sx={dialogSx}
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        m: 2,
                        width: "calc(100% - 32px)",
                        height: "calc(100% - 32px)",
                        maxWidth: "none",
                        maxHeight: "none",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        pr: 1,
                        py: 1.25,
                    }}
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
                        Piece map
                    </Typography>
                    <IconButton
                        size="small"
                        aria-label="Close piece map"
                        onClick={() => setMiniMapDialogOpen(false)}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    sx={{
                        pt: 0,
                        pb: 2,
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                    }}
                >
                    {/* Largest square that fits: height drives the size, width caps it in portrait. */}
                    <Box sx={{ height: "100%", aspectRatio: "1 / 1", maxWidth: "100%" }}>
                        <AdminColorBasketMiniMap
                            baskets={allBaskets}
                            imageUrl={imageUrl}
                            grid={grid}
                            size={null}
                            compact
                            oneBasedLabels
                            highlightBasketIndex={basketIndex}
                            emphasizeOnly
                        />
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );

    return (
        <>
            {simpleMode ? simpleLayout : (
            <Paper variant="outlined" sx={{ p: 2, width: "100%" }}>
                <Stack spacing={2} sx={{ width: "100%" }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Box
                            sx={{
                                width: 18,
                                height: 18,
                                borderRadius: 0.5,
                                bgcolor: hex,
                                border: "1px solid",
                                borderColor: "divider",
                            }}
                        />
                        <Typography variant="h6" sx={{ flex: 1, minWidth: 160 }}>
                            Basket {basketIndex} practice
                        </Typography>
                        <Chip
                            size="small"
                            label={`${homeCount}/${basketTotal} home`}
                            color={isSolved ? "success" : "default"}
                        />
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`${piecesInBasketTray.length} in tray`}
                        />
                        <Chip
                            size="small"
                            label={playState}
                            color={playState === PLAY_STATE_DEPLOYED ? "secondary" : "info"}
                            variant="outlined"
                        />
                        <Tooltip title="Place all tray pieces on the board at random">
                            <span>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={placeAllOnBoard}
                                    disabled={piecesInBasketTray.length === 0}
                                >
                                    All
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title="Place all tray pieces on the board at home">
                            <span>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={placeAllFitOnBoard}
                                    disabled={piecesInBasketTray.length === 0}
                                >
                                    All-fit
                                </Button>
                            </span>
                        </Tooltip>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={snapAllHome}
                            disabled={isSolved || boardPieces.length === 0}
                        >
                            Snap all home
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            disabled={!placeableGroup || placeBusy}
                            onClick={placeGroupOnMainBoard}
                        >
                            {placeBusy
                                ? "Placing…"
                                : placeableGroup
                                  ? `Place group (${placeableGroup.members.length}) on main`
                                  : "Place group on main"}
                        </Button>
                        <Button size="small" variant="outlined" onClick={resetBasket}>
                            Shuffle tray
                        </Button>
                        {!hideCloseButton ? (
                            <Button size="small" onClick={onClose}>
                                Close
                            </Button>
                        ) : null}
                    </Stack>

                    <Typography variant="caption" color="text.secondary">
                        Basket pieces start in the tray below the board. Drag them onto the board
                        (region guide matches the mini-map), connect neighbors, then place or deploy
                        onto the main board.
                    </Typography>

                    <BoardMatColorSwitcher
                        variant="row"
                        matId={boardMatId}
                        onMatIdChange={setBoardMatId}
                        ariaLabel="Board color"
                    />

                    <Grid container spacing={3} alignItems="flex-start" sx={{ width: "100%" }}>
                        <Grid size={{ xs: 12, md: 5 }}>
                            <AdminColorBasketMiniMap
                                baskets={allBaskets}
                                imageUrl={imageUrl}
                                grid={grid}
                                size={null}
                                highlightBasketIndex={basketIndex}
                                emphasizeOnly
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 7 }}>
                            <Box sx={{ width: "100%" }}>
                                <Box sx={{ mb: 1 }}>{renderBoardToolbar()}</Box>
                                {!boardFullscreen ? (
                                    <>
                                        {renderBoardCanvas()}
                                        <Box sx={{ mt: 2 }}>{renderBasketTray()}</Box>
                                    </>
                                ) : (
                                    <Box
                                        sx={{
                                            width: "100%",
                                            aspectRatio: "1 / 1",
                                            borderRadius: 1,
                                            border: 1,
                                            borderColor: "divider",
                                            bgcolor: "action.hover",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            Board open in full screen
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    </Grid>

                    {!hideMainBoard ? (
                    <Box
                        id="admin-color-basket-main-board"
                        sx={{
                            pt: 2,
                            mt: 1,
                            borderTop: 1,
                            borderColor: "divider",
                            width: "100%",
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ mb: 1.5 }}
                        >
                            <Typography variant="h6" sx={{ flex: 1, minWidth: 160 }}>
                                Main board
                            </Typography>
                            <Chip
                                size="small"
                                label={`${mainBoardPieces.length}/${puzzlePieceCount} on board`}
                                color={mainSolved ? "success" : "default"}
                            />
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`${piecesNotOnMain.length} in tray`}
                            />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                            Drag tray pieces onto the main board, move and connect them. Place-group
                            from the basket locks those pieces at home here.
                        </Typography>
                        <Grid container spacing={2} alignItems="flex-start">
                            <Grid size={{ xs: 12, md: 8 }}>
                                <Box
                                    sx={{
                                        width: "100%",
                                        aspectRatio: "1 / 1",
                                        borderRadius: 1,
                                        border: 1,
                                        borderColor: "divider",
                                        overflow: "hidden",
                                        position: "relative",
                                        bgcolor: "action.hover",
                                    }}
                                >
                                    {/* Absolute fill so fitParent gets a real height (aspect-ratio alone can collapse). */}
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            display: "flex",
                                            flexDirection: "column",
                                        }}
                                    >
                                        <CanvasBoard
                                            boardRef={mainBoardRef}
                                            boardPieces={mainBoardPieces}
                                            grid={grid}
                                            imageUrl={imageUrl}
                                            paths={paths}
                                            svgPathFallback={svgPathFallback}
                                            dragging={
                                                dragging?.boardLane === "main"
                                                    ? {
                                                          pieceIndex: dragging.pieceIndex,
                                                          boardId: dragging.boardId,
                                                          groupId: dragging.groupId,
                                                      }
                                                    : null
                                            }
                                            placedCount={mainBoardPieces.length}
                                            total={puzzlePieceCount}
                                            isSolved={mainSolved}
                                            onBoardPieceMouseDown={(e, pieceIndex, opts) =>
                                                startDrag(e, pieceIndex, {
                                                    boardLane: "main",
                                                    boardId: opts?.boardId,
                                                    boardX: opts?.boardX,
                                                    boardY: opts?.boardY,
                                                    groupId: opts?.groupId,
                                                })
                                            }
                                            readOnly={false}
                                            showPieceGridCoords={false}
                                            showHeader={false}
                                            showDieLines
                                            matPreset={boardMat}
                                            matColor={boardMat.hex}
                                            grooveStyle={boardGrooveStyle}
                                            fitParent
                                        />
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Box
                                    sx={{
                                        width: "100%",
                                        maxHeight: { md: 520 },
                                        overflow: "auto",
                                        borderRadius: 1,
                                        border: 1,
                                        borderColor: "divider",
                                        p: 1,
                                    }}
                                >
                                    <CanvasScattered
                                        scatteredRef={scatteredRef}
                                        piecesNotOnBoard={piecesNotOnMain}
                                        scatterPositions={scatterPositions}
                                        grid={grid}
                                        imageUrl={imageUrl}
                                        paths={paths}
                                        dragging={
                                            dragging?.boardLane === "main" && dragging?.fromScattered
                                                ? {
                                                      pieceIndex: dragging.pieceIndex,
                                                      fromScattered: true,
                                                  }
                                                : null
                                        }
                                        onScatteredPieceMouseDown={(e, pieceIndex, opts) =>
                                            startDrag(e, pieceIndex, {
                                                boardLane: "main",
                                                fromScattered: true,
                                                listTray: !!opts?.listTray,
                                                scatterX: opts?.scatterX,
                                                scatterY: opts?.scatterY,
                                            })
                                        }
                                        readOnly={false}
                                        showPieceGridCoords={false}
                                        showViewToggle
                                        trayDescription="Main tray — drag pieces onto the main board"
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                    ) : null}
                </Stack>
            </Paper>
            )}

            <Dialog
                fullScreen
                open={boardFullscreen && !simpleMode}
                onClose={() => setBoardFullscreen(false)}
                PaperProps={{
                    sx: {
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        maxHeight: "100%",
                        overflow: "hidden",
                        bgcolor: "background.paper",
                    },
                }}
            >
                <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
                    {renderBoardToolbar()}
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        p: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                        }}
                    >
                        {boardFullscreen ? renderBoardCanvas() : null}
                    </Box>
                    {boardFullscreen ? renderBasketTray() : null}
                </Box>
            </Dialog>

            {dragging && paths?.[dragging.pieceIndex] && (
                <Box
                    ref={dragGhostRef}
                    sx={{
                        position: "fixed",
                        left: dragging.elLeft,
                        top: dragging.elTop,
                        zIndex: 2500,
                        pointerEvents: "none",
                        transform: `scale(${dragging.ghostScale || 1})`,
                        transformOrigin: "top left",
                        opacity: 0.92,
                        willChange: "transform",
                    }}
                >
                    {isGroupGhost ? (
                        groupMembers.map((member) => (
                            <Box
                                key={member.pieceIndex}
                                sx={{
                                    position: "absolute",
                                    left: member.dx,
                                    top: member.dy,
                                }}
                            >
                                <PieceVisual
                                    pieceIndex={member.pieceIndex}
                                    imageUrl={imageUrl}
                                    grid={grid}
                                    paths={paths}
                                    anchorToCell
                                    showGridCoords={false}
                                />
                            </Box>
                        ))
                    ) : (
                        <PieceVisual
                            pieceIndex={dragging.pieceIndex}
                            imageUrl={imageUrl}
                            grid={grid}
                            paths={paths}
                            anchorToCell={!(dragging.fromScattered && dragging.listTray)}
                            showGridCoords={false}
                        />
                    )}
                </Box>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <Dialog
                open={deployDoneDialog.open}
                onClose={() =>
                    setDeployDoneDialog({
                        open: false,
                        message: "",
                        pieces: [],
                        placedCount: 0,
                    })
                }
                fullWidth
                maxWidth={promoCapture ? "sm" : "xs"}
                sx={dialogSx}
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        ...(promoCapture ? { maxWidth: 560 } : null),
                    },
                }}
            >
                <DialogTitle sx={{ fontWeight: 800, pb: 1, fontSize: promoCapture ? "1.5rem" : undefined }}>
                    On the main puzzle
                </DialogTitle>
                <DialogContent>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            mb: 1.5,
                            fontSize: promoCapture ? "1.15rem" : undefined,
                            lineHeight: 1.45,
                        }}
                    >
                        {deployDoneDialog.message ||
                            "These resolved pieces are now placed on the main puzzle."}
                    </Typography>
                    {(() => {
                        const previewPieces = Array.isArray(deployDoneDialog.pieces)
                            ? deployDoneDialog.pieces
                            : [];
                        if (!previewPieces.length || !(grid > 0) || !imageUrl || !paths) {
                            return null;
                        }
                        const cell = BOARD_SIZE / grid;
                        const tab = pieceVisualCellOffsetPx(cell);
                        let minX = Infinity;
                        let minY = Infinity;
                        let maxX = -Infinity;
                        let maxY = -Infinity;
                        for (const p of previewPieces) {
                            const x = Number(p?.x);
                            const y = Number(p?.y);
                            if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
                            minX = Math.min(minX, x);
                            minY = Math.min(minY, y);
                            maxX = Math.max(maxX, x + cell);
                            maxY = Math.max(maxY, y + cell);
                        }
                        if (!Number.isFinite(minX)) {
                            if (regionBoundsPx) {
                                minX = regionBoundsPx.left;
                                minY = regionBoundsPx.top;
                                maxX = regionBoundsPx.right;
                                maxY = regionBoundsPx.bottom;
                            } else {
                                return null;
                            }
                        }
                        const pad = cell * 0.35;
                        const contentW = Math.max(cell, maxX - minX) + pad * 2;
                        const contentH = Math.max(cell, maxY - minY) + pad * 2;
                        const previewMax = promoCapture ? 420 : 280;
                        const scale = Math.min(previewMax / contentW, previewMax / contentH, 1.25);
                        const viewW = Math.round(contentW * scale);
                        const viewH = Math.round(contentH * scale);
                        const originX = minX - pad;
                        const originY = minY - pad;

                        return (
                            <Box
                                sx={{
                                    mx: "auto",
                                    mb: 0.5,
                                    width: viewW,
                                    height: viewH,
                                    position: "relative",
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    bgcolor: boardMat?.hex || "#c5d5ce",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.35)",
                                }}
                                aria-label="Resolved pieces placed on the main puzzle"
                            >
                                {previewPieces.map((p) => {
                                    const idx = Number(p?.pieceIndex);
                                    if (!Number.isInteger(idx) || idx < 0) return null;
                                    const x = Number(p.x);
                                    const y = Number(p.y);
                                    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
                                    return (
                                        <Box
                                            key={idx}
                                            sx={{
                                                position: "absolute",
                                                left: (x - originX - tab) * scale,
                                                top: (y - originY - tab) * scale,
                                                width: 0,
                                                height: 0,
                                                lineHeight: 0,
                                                transform: `scale(${scale})`,
                                                transformOrigin: "top left",
                                            }}
                                        >
                                            <PieceVisual
                                                pieceIndex={idx}
                                                imageUrl={imageUrl}
                                                grid={grid}
                                                paths={paths}
                                                showGridCoords={false}
                                                anchorToCell={false}
                                            />
                                        </Box>
                                    );
                                })}
                            </Box>
                        );
                    })()}
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                            display: "block",
                            mt: 1.25,
                            textAlign: "center",
                            fontSize: promoCapture ? "1rem" : undefined,
                        }}
                    >
                        Continue on the main puzzle with these pieces already in place.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() => {
                            setDeployDoneDialog({
                                open: false,
                                message: "",
                                pieces: [],
                                placedCount: 0,
                            });
                            onBackToMainPlay?.();
                        }}
                        sx={{
                            bgcolor: "#3BA666",
                            color: "#fff",
                            fontWeight: 700,
                            py: promoCapture ? 1.5 : undefined,
                            fontSize: promoCapture ? "1.15rem" : undefined,
                            "&:hover": { bgcolor: "#2e7d32" },
                        }}
                    >
                        Back to main puzzle
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
